/* ============================================================
   TEAK TALE THEME by Impulsebyte (impulsebyte.com) — product-form.js  v2.0
   Handles: infinite-loop gallery, zoom lightbox, variant selection
   (URL sync + variant image switching), inventory-aware quantity,
   ATC, tabs, sticky ATC, PIN checker.
   ============================================================ */
(function () {
'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const formatMoney = (cents) => window.ttMoney(cents);

/* ─── PDP Gallery: infinite loop + scroll-snap + zoom lightbox ───
   - Slides live in a scroll-snap viewport; JS clones the first & last
     slide so swiping past either end loops seamlessly (infinite scroll).
   - Thumbnails (desktop) and dots (mobile) stay in sync.
   - Clicking a slide opens the zoom lightbox (close button, arrows,
     click-to-magnify, Esc / backdrop close).
   - goToImageId(id) lets the variant selector jump to a variant's image. */
class PdpGallery {
  constructor(root) {
    this.root = root;
    if (!this.root) return;
    this.viewport = $('#PdpgViewport', root);
    this.track = $('#PdpgTrack', root);
    this.slides = $$('.pdpg__slide', this.track);
    this.n = this.slides.length;
    this.thumbs = $$('[data-thumb-index]', root);
    this.dots = $$('[data-dot-index]', root);
    this.current = 0;
    this.loop = this.n > 1;
    this._suppress = false;
    // Lightbox
    this.lb = $('#Lightbox');
    // Move the zoom overlay to <body> so no sticky/transformed parent can trap it under other content
    if (this.lb && this.lb.parentElement !== document.body) document.body.appendChild(this.lb);
    this.lbImg = $('#LightboxImg');
    this.lbCounter = $('#LightboxCounter');
    this.lbIndex = 0;
    if (!this.viewport || !this.n) return;
    this._init();
  }

  _init() {
    if (this.loop) this._makeClones();
    // Position on the first real slide without animation
    this._jumpTo(this._domIndex(0));

    // Scroll → index sync (debounced; scrollend isn't in all browsers)
    on(this.viewport, 'scroll', debounce(() => this._onScrollSettled(), 90), { passive: true });

    // Thumbs / dots / arrows
    this.thumbs.forEach(t => {
      const i = parseInt(t.dataset.thumbIndex);
      on(t, 'click', () => this.goTo(i, true));
      // Warm the full-size image as soon as the shopper points at a thumbnail
      on(t, 'pointerenter', () => this._warm(i), { passive: true });
      on(t, 'focus', () => this._warm(i));
    });
    this.dots.forEach(d => on(d, 'click', () => this.goTo(parseInt(d.dataset.dotIndex), true)));
    // After the page has loaded, fetch the remaining gallery images in the background
    // so switching never shows a half-loaded image.
    const warmAll = () => this.slides.forEach((_, i) => this._warm(i));
    if (document.readyState === 'complete') setTimeout(warmAll, 300);
    else on(window, 'load', () => ('requestIdleCallback' in window ? requestIdleCallback(warmAll, { timeout: 2000 }) : setTimeout(warmAll, 500)));
    on($('[data-pdpg-prev]', this.root.parentElement || document), 'click', () => this.goTo(this.current - 1));
    on($('[data-pdpg-next]', this.root.parentElement || document), 'click', () => this.goTo(this.current + 1));

    // Keyboard on viewport
    on(this.viewport, 'keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.current - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.current + 1); }
    });

    // Open lightbox on slide click (but not while swiping)
    let downX = 0, downY = 0;
    on(this.viewport, 'pointerdown', e => { downX = e.clientX; downY = e.clientY; });
    on(this.viewport, 'click', e => {
      if (this.root.dataset.lightbox === 'false') return; // "Enable image lightbox" theme setting off
      if (Math.abs(e.clientX - downX) > 8 || Math.abs(e.clientY - downY) > 8) return; // was a drag
      const slide = e.target.closest('.pdpg__slide');
      if (slide) this.openLightbox(this.current);
    });

    // Re-sync position on resize (slide width changes)
    on(window, 'resize', debounce(() => this._jumpTo(this._domIndex(this.current)), 150), { passive: true });

    this._initLightbox();
    this._setActive(0);
  }

  /* Clone last → front, first → end, so the strip is: [cloneLast, 0..n-1, cloneFirst] */
  _makeClones() {
    const first = this.slides[0], last = this.slides[this.n - 1];
    const cLast = last.cloneNode(true), cFirst = first.cloneNode(true);
    [cLast, cFirst].forEach(c => { c.dataset.clone = 'true'; c.setAttribute('aria-hidden', 'true'); $$('img', c).forEach(i => { i.loading = 'lazy'; i.fetchPriority = 'low'; }); });
    this.track.insertBefore(cLast, first);
    this.track.appendChild(cFirst);
  }

  _slideW() { return this.viewport.clientWidth; }
  /* DOM position of a real index inside the (possibly clone-padded) track */
  _domIndex(realIdx) { return this.loop ? realIdx + 1 : realIdx; }

  _jumpTo(domIdx) {
    this.viewport.style.scrollBehavior = 'auto';
    this.viewport.scrollLeft = domIdx * this._slideW();
    // restore smooth on next frame
    requestAnimationFrame(() => { this.viewport.style.scrollBehavior = ''; });
  }

  _onScrollSettled() {
    if (this._suppress) return;
    const domIdx = Math.round(this.viewport.scrollLeft / this._slideW());
    if (!this.loop) { this._setActive(Math.max(0, Math.min(this.n - 1, domIdx))); return; }
    if (domIdx === 0) {              // landed on cloneLast → teleport to real last
      this._jumpTo(this._domIndex(this.n - 1));
      this._setActive(this.n - 1);
    } else if (domIdx === this.n + 1) { // landed on cloneFirst → teleport to real first
      this._jumpTo(this._domIndex(0));
      this._setActive(0);
    } else {
      this._setActive(domIdx - 1);
    }
  }

  _warm(realIdx) {
    const img = $('img', this.slides[realIdx]);
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  /* instant = true: jump straight to the image (thumbnails, dots, variant change). */
  goTo(realIdx, instant = false) {
    if (!this.n) return;
    if (instant) {
      const idx = ((realIdx % this.n) + this.n) % this.n;
      this._warm(idx);
      this._suppress = true;
      this._jumpTo(this._domIndex(idx));
      this._setActive(idx);
      clearTimeout(this._supT);
      this._supT = setTimeout(() => { this._suppress = false; }, 150);
      return;
    }
    this._warm(((realIdx % this.n) + this.n) % this.n);
    if (this.loop) {
      // allow -1 / n to scroll into the clones for a seamless wrap
      const domIdx = realIdx + 1;
      this.viewport.scrollTo({ left: domIdx * this._slideW(), behavior: 'smooth' });
      // _onScrollSettled handles the teleport + active state
    } else {
      const idx = Math.max(0, Math.min(this.n - 1, realIdx));
      this.viewport.scrollTo({ left: idx * this._slideW(), behavior: 'smooth' });
    }
  }

  goToImageId(imageId) {
    if (!imageId) return;
    const idx = this.slides.findIndex(s => s.dataset.imageId === String(imageId));
    if (idx !== -1 && idx !== this.current) this.goTo(idx, true);
  }

  _setActive(realIdx) {
    this.current = realIdx;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === realIdx));
    // also mark matching clones for the animation state
    $$('[data-clone]', this.track).forEach(c => {
      c.classList.toggle('is-active', c.dataset.slideIndex === String(realIdx));
    });
    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === realIdx);
      t.setAttribute('aria-selected', i === realIdx ? 'true' : 'false');
      if (i === realIdx) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === realIdx));
  }

  /* ── Lightbox ── */
  _initLightbox() {
    if (!this.lb) return;
    on($('#LightboxClose'), 'click', () => this.closeLightbox());
    on($('#LightboxPrev'), 'click', e => { e.stopPropagation(); this._lbGo(this.lbIndex - 1); });
    on($('#LightboxNext'), 'click', e => { e.stopPropagation(); this._lbGo(this.lbIndex + 1); });
    on(this.lb, 'click', e => { if (e.target === this.lb || e.target.id === 'LightboxStage') this.closeLightbox(); });
    on(document, 'keydown', e => {
      if (!this.lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this._lbGo(this.lbIndex - 1);
      if (e.key === 'ArrowRight') this._lbGo(this.lbIndex + 1);
    });
    // Click image to magnify from the click point
    on(this.lbImg, 'click', e => {
      e.stopPropagation();
      const zoomed = this.lbImg.classList.toggle('is-zoomed');
      if (zoomed) {
        const r = this.lbImg.getBoundingClientRect();
        this.lbImg.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      }
    });
    // Swipe in lightbox
    let sx = 0;
    on(this.lb, 'touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    on(this.lb, 'touchend', e => {
      const diff = sx - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50 && !this.lbImg.classList.contains('is-zoomed')) this._lbGo(this.lbIndex + (diff > 0 ? 1 : -1));
    });
  }

  openLightbox(idx) {
    if (!this.lb) return;
    this._lbGo(idx);
    this.lb.classList.add('is-open');
    this.lb.setAttribute('aria-hidden', 'false');
    this.lb.inert = false;
    document.body.style.overflow = 'hidden';
    $('#LightboxClose')?.focus();
  }

  closeLightbox() {
    this.lb?.classList.remove('is-open');
    this.lb?.setAttribute('aria-hidden', 'true');
    if (this.lb) this.lb.inert = true;
    this.lbImg?.classList.remove('is-zoomed');
    document.body.style.overflow = '';
    this.viewport?.focus({ preventScroll: true });
  }

  _lbGo(idx) {
    const n = this.n;
    this.lbIndex = ((idx % n) + n) % n; // wrap
    const slide = this.slides[this.lbIndex];
    this.lbImg.classList.remove('is-zoomed');
    this.lbImg.src = slide?.dataset.zoomSrc || $('img', slide)?.src || '';
    if (this.lbCounter) this.lbCounter.textContent = `${this.lbIndex + 1} / ${n}`;
    // keep the gallery in sync behind the lightbox
    if (this.lbIndex !== this.current) this.goTo(this.lbIndex, true);
  }
}

/* ─── Variant Selector ─── */
class VariantSelector {
  constructor(el) {
    this.el = el;
    if (!this.el) return;
    this.productData = JSON.parse($('#ProductJSON')?.textContent || '{}');
    this.inventory = JSON.parse($('#VariantInventory')?.textContent || '{}');
    this.LOW_STOCK = 5;
    this.variants = this.productData.variants || [];
    this.options = this.productData.options || [];
    this.currentVariant = this.variants.find(v => v.id === parseInt($('#SelectedVariantId')?.value)) || this.variants[0];
    this.gallery = window._pdpGallery;
    this._init();
  }
  _init() {
    on(this.el, 'click', e => {
      const btn = e.target.closest('[data-option-btn]');
      if (btn && !btn.disabled) this._selectOption(btn);
    });
    on(this.el, 'change', e => {
      if (e.target.closest('[data-option-select]')) this._findVariant();
    });
    // Honour ?variant= in the URL on load (deep links / refresh)
    const urlVariant = parseInt(new URLSearchParams(location.search).get('variant'));
    if (urlVariant) {
      const v = this.variants.find(x => x.id === urlVariant);
      if (v) { this.currentVariant = v; this._syncButtonsToVariant(v); }
    }
    this._updateUI(this.currentVariant);
  }
  _syncButtonsToVariant(variant) {
    (variant.options || []).forEach((val, i) => {
      $$(`[data-option-position="${i+1}"]`, this.el).forEach(b => {
        b.classList.toggle('is-selected', b.dataset.optionValue === val);
        b.setAttribute('aria-pressed', b.dataset.optionValue === val ? 'true' : 'false');
      });
      const label = $(`[data-option-selected="${i+1}"]`, this.el);
      if (label) label.textContent = val;
    });
  }
  _selectOption(btn) {
    const position = parseInt(btn.dataset.optionPosition);
    const value = btn.dataset.optionValue;
    $$(`[data-option-position="${position}"]`, this.el).forEach(b => { b.classList.remove('is-selected'); b.setAttribute('aria-pressed', 'false'); });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    const label = $(`[data-option-selected="${position}"]`, this.el);
    if (label) label.textContent = value;
    this._findVariant();
  }
  _findVariant() {
    const selected = [];
    this.options.forEach((opt, i) => {
      const btn = $(`[data-option-position="${i+1}"].is-selected`, this.el);
      const dropdown = $(`[data-option-select][data-option-position="${i+1}"]`, this.el);
      selected.push(btn?.dataset.optionValue || dropdown?.value || '');
    });
    const variant = this.variants.find(v => v.options.every((o, i) => o === selected[i]));
    if (variant) { this.currentVariant = variant; this._updateUI(variant); }
  }
  _stockFor(variant) { return this.inventory[String(variant.id)] || null; }
  _updateUI(variant) {
    if (!variant) return;
    const inp = $('#SelectedVariantId');
    if (inp) inp.value = variant.id;
    document.dispatchEvent(new CustomEvent('tt:variant:change', { detail: { variant } }));

    // Price
    const priceEl = $('#PdpPrice');
    const compareEl = $('#PdpCompare');
    const badgeEl = $('#PdpBadge');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      if (variant.compare_at_price > variant.price) { compareEl.textContent = formatMoney(variant.compare_at_price); compareEl.style.display = ''; }
      else compareEl.style.display = 'none';
    }
    if (badgeEl) {
      if (variant.compare_at_price > variant.price) {
        badgeEl.textContent = Math.round((1 - variant.price / variant.compare_at_price) * 100) + '% OFF';
        badgeEl.style.display = '';
      } else badgeEl.style.display = 'none';
    }
    // "You save ₹X" — concrete savings convert better than a bare percentage
    const saveEl = $('#PdpSave');
    if (saveEl) {
      if (variant.compare_at_price > variant.price) {
        saveEl.textContent = `You save ${formatMoney(variant.compare_at_price - variant.price)}`;
        saveEl.style.display = '';
      } else saveEl.style.display = 'none';
    }

    // ATC buttons
    const atcBtn = $('#AtcBtn'), stickyAtcBtn = $('#StickyAtcBtn'), buyNow = $('#BuyNowBtn');
    [atcBtn, stickyAtcBtn].forEach(b => {
      if (!b) return;
      b.disabled = !variant.available;
      b.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    });
    if (buyNow) buyNow.disabled = !variant.available;

    // Stock badge + low-stock urgency (from real inventory data)
    const stock = this._stockFor(variant);
    const stockBadge = $('#StockBadge');
    const lowStock = stock && stock.managed && stock.policy === 'deny' && stock.qty > 0 && stock.qty <= this.LOW_STOCK;
    if (stockBadge) {
      if (!variant.available) {
        stockBadge.textContent = 'Out of Stock';
        stockBadge.style.color = '#ef4444';
      } else if (lowStock) {
        stockBadge.textContent = `Only ${stock.qty} left in stock`;
        stockBadge.style.color = '#f97316';
      } else {
        stockBadge.textContent = '✓ In Stock, ships in 2-3 days';
        stockBadge.style.color = '';
      }
    }

    // Quantity selector: clamp to available inventory
    const qtyInput = $('#QtyInput');
    const qtyHint = $('#QtyStockHint');
    if (qtyInput) {
      const max = (stock && stock.managed && stock.policy === 'deny') ? Math.max(1, stock.qty) : 9999;
      qtyInput.max = max;
      if (parseInt(qtyInput.value) > max) qtyInput.value = max;
      const plusBtn = qtyInput.parentElement?.querySelector('[data-qty-plus]');
      if (plusBtn) plusBtn.disabled = parseInt(qtyInput.value) >= max;
    }
    if (qtyHint) qtyHint.textContent = lowStock ? `Only ${stock.qty} available` : '';

    // Variant image: jump the gallery to this variant's image
    (this.gallery || window._pdpGallery)?.goToImageId(variant.featured_image?.id);

    // URL reflects the selected variant (shareable / refresh-safe)
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    const stickyPrice = $('#StickyAtcPrice');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    this._markUnavailable();
  }
  _markUnavailable() {
    $$('[data-option-btn]', this.el).forEach(btn => {
      const pos = parseInt(btn.dataset.optionPosition);
      const val = btn.dataset.optionValue;
      const testOptions = this.options.map((_, i) => {
        if (i + 1 === pos) return val;
        const sel = $(`[data-option-position="${i+1}"].is-selected`, this.el);
        return sel?.dataset.optionValue || this.options[i];
      });
      const available = this.variants.some(v => v.options.every((o, i) => o === testOptions[i]) && v.available);
      btn.toggleAttribute('disabled', !available);
    });
  }
}

/* ─── Add to Cart Form ─── */
class ProductForm {
  constructor(el) {
    this.el = el;
    if (!this.el) return;
    this.atcBtn = $('#AtcBtn');
    this.buyNowBtn = $('#BuyNowBtn');
    this.qtyInput = $('#QtyInput');
    this._init();
  }
  _init() {
    on(this.el, 'submit', e => { e.preventDefault(); this._addToCart().catch(() => {}); });
    on(this.buyNowBtn, 'click', async e => {
      e.preventDefault();
      try { await this._addToCart(true); } catch (err) { return; }
      window.location.href = '/checkout';
    });
    on($('[data-qty-minus]', this.el), 'click', e => { e.preventDefault(); this._changeQty(-1); });
    on($('[data-qty-plus]', this.el), 'click', e => { e.preventDefault(); this._changeQty(1); });
    on(this.qtyInput, 'change', () => this._changeQty(0));
    on($('#StickyAtcBtn'), 'click', () => this._addToCart().catch(() => {}));
  }
  _changeQty(delta) {
    if (!this.qtyInput) return;
    const max = parseInt(this.qtyInput.max) || 9999;
    const val = Math.max(1, Math.min(max, (parseInt(this.qtyInput.value) || 1) + delta));
    this.qtyInput.value = val;
    const minus = $('[data-qty-minus]', this.el), plus = $('[data-qty-plus]', this.el);
    if (minus) minus.disabled = val <= 1;
    if (plus) plus.disabled = val >= max;
    document.dispatchEvent(new CustomEvent('tt:qty:change', { detail: { qty: val } }));
  }
  /* Personalisation fields (Custom text / Photo upload blocks). Returns false if something is missing. */
  _checkCustom() {
    const fields = $$('[data-custom-field] input, [data-custom-field] textarea', this.el);
    for (const f of fields) {
      if (f.type !== 'file' && f.value) f.value = f.value.trim();
      if (!f.checkValidity()) {
        f.closest('[data-custom-field]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => { f.reportValidity(); if (f.type !== 'file') f.focus({ preventScroll: true }); }, 350);
        return false;
      }
    }
    return true;
  }
  _customPayload(variantId, qty) {
    const fields = $$('[data-custom-field] input, [data-custom-field] textarea', this.el);
    const props = {}; let file = false;
    const fd = new FormData();
    fd.append('id', variantId); fd.append('quantity', qty);
    fields.forEach(f => {
      if (!f.name) return;
      if (f.type === 'file') { if (f.files[0]) { fd.append(f.name, f.files[0]); file = true; } return; }
      if (!f.value) return;
      fd.append(f.name, f.value);
      props[f.name.replace(/^properties\[|\]$/g, '')] = f.value;
    });
    return { props, fd: file ? fd : null };
  }
  async _addToCart(silent = false) {
    const variantId = parseInt($('#SelectedVariantId')?.value);
    const qty = parseInt(this.qtyInput?.value || 1);
    if (!variantId) return;
    if (!this._checkCustom()) throw new Error('custom-invalid');
    const { props, fd } = this._customPayload(variantId, qty);
    const btn = this.atcBtn;
    if (btn) { btn.disabled = true; btn.textContent = fd ? 'Uploading photo…' : 'Adding…'; btn.classList.add('btn--loading'); }
    try {
      if (fd) await window.cartDrawer?.addFormData(fd);
      else await window.cartDrawer?.addItem(variantId, qty, props);
      if (btn) { btn.textContent = 'Added! ✓'; btn.classList.remove('btn--loading'); btn.classList.add('btn--success'); setTimeout(() => { btn.textContent = 'Add to Cart'; btn.classList.remove('btn--success'); btn.disabled = false; }, 2000); }
      if (!silent) window.cartDrawer?.afterAdd();
    } catch(err) {
      if (btn) { btn.textContent = 'Add to Cart'; btn.classList.remove('btn--loading'); btn.disabled = false; }
      if (window.toast) window.toast.show(err.message || 'Could not add to cart', 'error');
      throw err;
    }
  }
}

/* ─── Quantity discount tiers: live prices per variant, highlight the active tier, tap to set quantity ─── */
function initQtyDiscount(root = document) {
  $$('[data-qty-discount]', root).forEach(box => {
    if (box._qd) return; box._qd = true;
    const btns = $$('[data-qd-min]', box);
    const qtyInput = $('#QtyInput');
    const setActive = qty => btns.forEach(b => {
      const min = +b.dataset.qdMin, max = +b.dataset.qdMax;
      const on = qty >= min && (!max || qty <= max);
      b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const setPrices = price => btns.forEach(b => {
      const el = b.querySelector('[data-qd-each]');
      if (el) el.textContent = formatMoney(Math.floor(price * (100 - +b.dataset.qdPct) / 100));
    });
    const box_in = box.querySelector('[data-qd-input]');
    const summary = box.querySelector('[data-qd-summary]');
    const tiers = btns.map(b => ({ min: +b.dataset.qdMin, max: +b.dataset.qdMax, pct: +b.dataset.qdPct }));
    let price = +box.dataset.price || 0;
    const tierFor = q => tiers.find(t => q >= t.min && (!t.max || q <= t.max));
    const summarise = qty => {
      if (!summary) return;
      const t = tierFor(qty);
      if (t) {
        const full = price * qty, total = Math.floor(full * (100 - t.pct) / 100);
        summary.innerHTML = `Total <b>${formatMoney(total)}</b> for ${qty}. You save <b>${formatMoney(full - total)}</b> (${t.pct}% off)`;
      } else {
        const next = tiers.filter(x => x.min > qty).sort((a, b) => a.min - b.min)[0];
        summary.innerHTML = next ? `Add <b>${next.min - qty}</b> more to get <b>${next.pct}% off</b>` : '';
      }
    };
    const sync = (qty, fromBox) => {
      setActive(qty); summarise(qty);
      if (box_in && !fromBox && document.activeElement !== box_in) box_in.value = qty;
    };
    const pushQty = qty => {
      if (!qtyInput) { sync(qty); return; }
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      sync(parseInt(qtyInput.value) || qty);
    };
    btns.forEach(b => on(b, 'click', () => {
      pushQty(+b.dataset.qdMin);
      if (box_in) box_in.value = qtyInput ? qtyInput.value : b.dataset.qdMin;
    }));
    if (box_in) {
      on(box_in, 'input', () => {
        const n = parseInt(box_in.value);
        if (n >= 1) { if (qtyInput) qtyInput.value = n; sync(n, true); }
      });
      on(box_in, 'change', () => {
        const n = Math.max(1, parseInt(box_in.value) || 1);
        pushQty(n); box_in.value = qtyInput ? qtyInput.value : n;
      });
      on(box_in, 'keydown', e => { if (e.key === 'Enter') { e.preventDefault(); box_in.dispatchEvent(new Event('change')); } });
      $$('[data-qd-step]', box).forEach(s => on(s, 'click', () => {
        const n = Math.max(1, (parseInt(box_in.value) || 1) + +s.dataset.qdStep);
        pushQty(n); box_in.value = qtyInput ? qtyInput.value : n;
      }));
    }
    on(document, 'tt:qty:change', e => sync(e.detail.qty));
    on(qtyInput, 'input', () => sync(parseInt(qtyInput.value) || 1));
    on(document, 'tt:variant:change', e => {
      if (!e.detail.variant) return;
      price = e.detail.variant.price; setPrices(price); summarise(parseInt(qtyInput?.value) || 1);
    });
    sync(parseInt(qtyInput?.value) || 1);
  });
}

/* ─── Personalisation fields: character counter + photo preview and size check ─── */
function initCustomFields(root = document) {
  $$('[data-custom-count]', root).forEach(inp => {
    const out = inp.closest('[data-custom-field]')?.querySelector('[data-custom-used]');
    if (!out) return;
    const upd = () => { out.textContent = inp.value.length; };
    on(inp, 'input', upd); upd();
  });
  $$('[data-custom-upload]', root).forEach(box => {
    const input = box.querySelector('input[type=file]');
    const empty = box.querySelector('.pdp-upload__empty');
    const prev = box.querySelector('.pdp-upload__preview');
    const img = box.querySelector('[data-upload-img]');
    const name = box.querySelector('[data-upload-name]');
    const err = box.querySelector('[data-upload-error]');
    const maxMb = parseFloat(box.dataset.maxMb) || 10;
    let url = null;
    const reset = () => {
      input.value = ''; input.setCustomValidity('');
      if (url) { URL.revokeObjectURL(url); url = null; }
      prev.hidden = true; empty.hidden = false; box.classList.remove('has-file');
    };
    const fail = msg => { reset(); err.textContent = msg; err.hidden = false; };
    on(input, 'change', () => {
      err.hidden = true;
      const f = input.files[0];
      if (!f) return reset();
      if (!/^image\//.test(f.type) && !/\.(heic|heif)$/i.test(f.name)) return fail('Please choose an image file (JPG, PNG, WEBP or HEIC).');
      if (f.size > maxMb * 1048576) return fail(`That photo is ${(f.size / 1048576).toFixed(1)} MB. Please choose one under ${maxMb} MB.`);
      if (url) URL.revokeObjectURL(url);
      url = URL.createObjectURL(f);
      img.src = url; name.textContent = f.name;
      empty.hidden = true; prev.hidden = false; box.classList.add('has-file');
    });
    on(box.querySelector('[data-upload-remove]'), 'click', e => { e.preventDefault(); reset(); input.click(); });
    ['dragenter', 'dragover'].forEach(t => on(box, t, e => { e.preventDefault(); box.classList.add('is-drag'); }));
    ['dragleave', 'drop'].forEach(t => on(box, t, () => box.classList.remove('is-drag')));
    on(box, 'drop', e => {
      e.preventDefault();
      if (!e.dataTransfer?.files?.length) return;
      input.files = e.dataTransfer.files;
      input.dispatchEvent(new Event('change'));
    });
  });
}

/* ─── Tab Switcher ─── */
class TabSwitcher {
  constructor(el) {
    this.el = el;
    if (!this.el) return;
    this.btns = $$('.pdp-tab-btn', el);
    this.panels = $$('.pdp-tab-panel', el);
    this._init();
  }
  _init() {
    this.btns.forEach(btn => {
      on(btn, 'click', () => {
        const target = btn.dataset.tab;
        this.btns.forEach(b => { b.classList.toggle('is-active', b.dataset.tab === target); b.setAttribute('aria-selected', b.dataset.tab === target ? 'true' : 'false'); });
        this.panels.forEach(p => p.classList.toggle('is-active', p.id === target));
        if (target === 'tab-reviews') setTimeout(() => $$('.review-bar__fill').forEach(el => { const w = el.dataset.width; if (w) el.style.width = w + '%'; }), 80);
      });
    });
    // "(x reviews)" link jumps to the reviews tab
    $$('[data-tab-link]').forEach(link => on(link, 'click', e => {
      e.preventDefault();
      const target = link.dataset.tabLink;
      const btn = this.btns.find(b => b.dataset.tab === target);
      if (btn) { btn.click(); btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    }));
  }
}

/* ─── Sticky ATC ─── */
class StickyATC {
  constructor() {
    this.bar = $('#StickyAtc');
    if (!this.bar) return;
    const form = $('#PdpForm');
    if (form && 'IntersectionObserver' in window) {
      // IO instead of a scroll listener: show the bar when the form leaves the viewport
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => this.bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.bottom < 0));
      }, { threshold: 0 });
      io.observe(form);
    } else {
      on(window, 'scroll', () => {
        const bottom = form?.getBoundingClientRect().bottom;
        this.bar.classList.toggle('is-visible', bottom !== undefined && bottom < 0);
      }, { passive: true });
    }
  }
}

/* ─── PIN Checker ─── */
class PINChecker {
  constructor() {
    on($('#PinCheckBtn'), 'click', () => this._check());
    on($('#PinInput'), 'keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this._check(); } });
  }
  _check() {
    const pin = $('#PinInput')?.value.trim();
    const result = $('#PinResult');
    if (!pin || pin.length !== 6 || !/^\d+$/.test(pin)) { if (result) { result.textContent = 'Please enter a valid 6-digit PIN.'; result.style.color = '#ef4444'; result.classList.add('is-visible'); } return; }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const d = new Date(); d.setDate(d.getDate() + 5);
    const label = days[d.getDay()] + ', ' + d.toLocaleDateString('en-IN', { day:'numeric', month:'short' });
    if (result) { result.innerHTML = `✓ Free delivery by <strong>${label}</strong>`; result.style.color = '#16a34a'; result.classList.add('is-visible'); }
  }
}

/* ─── Init ─── */
document.addEventListener('DOMContentLoaded', () => {
  const pdpEl = $('#PdpPage');
  if (!pdpEl) return;
  window._pdpGallery = new PdpGallery($('#PdpGallery'));
  window._variantSel = new VariantSelector($('#VariantSelector'));
  window._pdpForm = new ProductForm($('#PdpForm'));
  window._tabs = new TabSwitcher($('#PdpTabs'));
  window._stickyAtc = new StickyATC();
  window._pin = new PINChecker();
  initCustomFields(pdpEl);
  initQtyDiscount(pdpEl);
});
/* Theme editor: blocks added or edited re-render the section, so wire new fields up again */
document.addEventListener('shopify:section:load', e => { initCustomFields(e.target); initQtyDiscount(e.target); });

})(); // end IIFE
